import { Location } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostBinding, Input, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationEnd } from '@angular/router';
import { ApiLiveStream, ApiNotification, ApiShow, ApiUser, NotificationsApi } from '@tytapp/api';
import { AppConfig, Page, PageComponent, PagedArray, PageService, SiteWideAlert, sleep, Transfer } from '@tytapp/common';
import { NotificationItem, NotificationsService } from '@tytapp/notifications';
import { UserService } from '@tytapp/user';
import { UserAuthMediatorComponent } from '@tytapp/user/user-auth-mediator/user-auth-mediator.component';
import { ShowsSidebarComponent } from 'common-ui';
import { LiveStreamsService } from 'common-ui/live-streams.service';
import { environment } from 'environment';
import { GoogleCastService } from 'media-playback';
import { Subscription } from 'rxjs';
import { MessageDialogComponent } from '../message-dialog/message-dialog.component';

export interface NavItem {
    id: string;
    label: string;
    url: string;
    external?: boolean;
    audience?: ('visitor' | 'registered' | 'member' | 'staff')[];
    class?: string;
}

@Page('tyt-nav')
@Component({
    selector: 'tyt-nav',
    templateUrl: './nav.component.html',
    styleUrls: ['./nav.component.scss']
})
export class NavComponent extends PageComponent implements AfterViewInit {
    // TODO: this class should not be PageComponent but it relies on state transfer

    constructor(
        private pageService: PageService,
        private location: Location,
        private element: ElementRef,
        private userService: UserService,
        private liveStreamsService: LiveStreamsService,
        private castService: GoogleCastService,
        private appConfig: AppConfig,
        private notifications: NotificationsService,
        private domSanitizer: DomSanitizer,
        private notificationApi: NotificationsApi
    ) {
        super(pageService);
        this.subscription = this.router.events.subscribe(s => {
            if (s instanceof NavigationEnd) {
                this.update();
            }
        });
    }

    primaryMenu: NavItem[] = [
        { id: 'home', label: 'Home', url: '/' },
        { id: 'live', label: 'Live', url: '/live' },
        { id: 'shows', label: 'Shows', url: '/shows' },
        { id: 'podcasts', label: 'Podcasts', url: '/podcasts' },
        { id: 'stories', label: 'Stories', url: '/stories' }
    ];

    secondaryMenu: NavItem[] = [
        {
            id: 'community',
            label: 'Community',
            url: '/community'
        },
        {
            id: 'shop',
            label: 'Shop',
            url: 'https://shoptyt.com/',
            external: true
        },
        {
            id: 'donate',
            label: 'Donate',
            url: environment.urls.donate,
            audience: ['member', 'staff'],
            external: true
        },
        {
            id: 'join',
            label: 'Join',
            url: environment.urls.purchase,
            audience: ['visitor'],
            external: true
        },
        {
            id: 'sign-in',
            label: 'Sign in',
            url: '/login',
            audience: ['visitor'],
        }
    ];

    @HostBinding('class.animate-in')
    animateIn = false;

    get casting() {
        return this.castService.connected;
    }

    get pageComponentId() {
        return 'tyt-nav';
    }

    dismissAlert(alert: SiteWideAlert) {
        this.shell.dismissAlert(alert.id);
    }

    get audience() {
        if (!this.user)
            return 'visitor';
        if (this.user.staff)
            return 'staff';
        if (this.user.entitled)
            return 'member';

        return 'registered';
    }

    shouldShowNav(item: NavItem) {
        if (item.audience && !item.audience.includes(this.audience))
            return false;
        return true;
    }

    sidebarVisible: boolean = false;
    sidebarHidden: boolean = true;
    highlightMap = {};
    siteWideMessage = '';
    siteWideUrl: SafeHtml = null;
    notificationsEnabled = false;
    notificationsEnabledAlways = false;

    private subscription: Subscription;

    @ViewChildren(ShowsSidebarComponent)
    showsSidebars: QueryList<ShowsSidebarComponent>;

    async signOut() {
        this.userService.completeLogout();
        this.userService.user = null;
        this.router.navigate(['home']);
    }

    get showNotifications() {
        if (!this.notificationsEnabled)
            return false;
        return this.notifications.count > 0 || this.notificationsEnabledAlways;
    }

    get accountsUrl() {
        return environment.urls.accounts;
    }

    get userDisplayName() {
        if (!this.user)
            return null;

        if (this.user.profile) {
            if (this.user.profile.display_name)
                return this.user.profile.display_name;

            if (this.user.profile.first_name)
                return this.user.profile.first_name;
        }

        if (this.user.display_name)
            return this.user.display_name;

        return this.user.email;
    }

    alerts: SiteWideAlert[] = [];

    communityEnabled = false;

    toggleSidebar() {
        if (this.sidebarVisible)
            this.hideSidebar();
        else
            this.showSidebar();

        this.notifications.closeList();
    }

    finishSidebarChange;

    showSidebar() {
        this.sidebarHidden = false;
        clearTimeout(this.finishSidebarChange)
        this.finishSidebarChange = setTimeout(() => this.sidebarVisible = true);
    }

    hideSidebar() {
        // a11y: Important to fully hide the sidebar so that its removed from the tab order when not visible
        this.sidebarVisible = false;
        clearTimeout(this.finishSidebarChange)
        this.finishSidebarChange = setTimeout(() => this.sidebarHidden = true, 1000);
    }

    async init() {
        this.shell.alertsChanged.subscribe(alerts => this.alerts = alerts);
        this.notifications.visibleChange.subscribe(visible => {
            if (visible) {
                this.hideSidebar();
            }
        });

        this.communityEnabled = await this.appConfig.featureEnabled('apps.web.enable_community');
        this.notificationsEnabled = await this.appConfig.featureEnabled('apps.web.enable_notifications');
        this.notificationsEnabledAlways = await this.appConfig.featureEnabled('apps.web.enable_notifications_always');

        this.router.events.subscribe(ev => {
            this.hideSidebar();
        });

        await this.userService.ready;

        this.subscribe(this.userService.userChanged, (user) => {
            if (!this.hasServerState) {
                if (user && user.membership) {
                    if (user.membership.payment_status === 'past_due') {
                        this.isPastDue = true;
                    }
                }
                this.user = user;
                this.loadNotifications();
                this.csrNotificationsUpdate();
            }
        });

        try {
            this.isLive = await this.liveStreamsService.getLiveIndicator();
            this.updateNavItem('live', { class: this.isLive ? 'live' : undefined });
        } catch (e) {
            console.error(`While fetching the live streams in NavComponent:`);
            console.error(e);
        }

        await sleep(1);
        this.animateIn = true;
    }

    get alertWideUrl() {
        return this.siteWideUrl || 'javascript:;';
    }

    getNavItem(id: string) {
        return [].concat(this.primaryMenu, this.secondaryMenu).find(x => x.id === id);
    }

    updateNavItem(id: string, update: Partial<NavItem>) {
        let item = this.getNavItem(id);
        if (!item)
            return;

        Object.assign(item, update);
        this.itemClassCache.delete(item);
    }

    itemClassCache = new WeakMap<NavItem, any>();

    classesForItem(item: NavItem) {
        if (!this.itemClassCache.has(item)) {
            this.itemClassCache.set(item, {
                [item.class]: true,
                highlight: this.highlightMap[item.id]
            });
        }

        return this.itemClassCache.get(item);
    }

    openSiteWideUrlWindow() {
        if (typeof window !== 'undefined') {
            window.location.href = 'www.youtube.com';
        }
    }

    showsDropdownVisible: boolean = false;

    revealShowsDropdown() {
        if (this.showsDropdownVisible) {
            this.router.navigate(['shows']);
        } else {
            this.showsDropdownVisible = true;

            let element: HTMLElement = this.element.nativeElement;
            let dropContentElement = element.querySelector('.drop-content');

            if (this.showsSidebars && this.showsSidebars.length > 0)
                this.showsSidebars.first.reset();

            setTimeout(() => {
                dropContentElement.scrollTo({ top: 0 });
            }, 1);
        }
    }

    hideShowsDropdown(closeIconTapped = false) {
        this.showsDropdownVisible = false;
    }

    update() {
        let currentUrl = this.router.url;
        let urlArray = currentUrl.split('/');
        this.highlightMap = {};

        if (urlArray.length >= 2 && ['shows', 'watch', 'listen'].includes(urlArray[1])) {
            this.highlightMap['shows'] = true;
        }

        this.hideShowsDropdown();
    }

    @Transfer()
    user: ApiUser;

    @Transfer()
    isLive: boolean = false;

    @Transfer()
    isPastDue: boolean = false;

    ngAfterViewInit() {
    }

    destroy(): any { this.subscription.unsubscribe(); }

    @Input()
    menuOpen: boolean = false;

    @Input()
    noLayout: boolean = false;

    @Transfer()
    shows: ApiShow[];

    @Transfer()
    podcasts: ApiShow[];

    @Transfer()
    streams: ApiLiveStream[];

    get joinUrl() {
        // CAUTION: THIS SHOULD BE CHANGED GLOBALLY (at appConfig) NOT HERE
        return environment.urls.purchase;
    }

    get platformUrl() {
        return `${environment.urls.platform}`;
    }

    get accountUrl() {
        return `${environment.urls.accounts}/account`;
    }

    get communicationPrefUrl() {
        return `${environment.urls.accounts}/account/communication-pref`;
    }

    get profileUrl() {
        return `${environment.urls.accounts}/profile`;
    }

    @ViewChild('menu', { static: true })
    menu: ElementRef;

    subsection: string = '';
    private streamsFetched = false;

    private pendingShow = null;
    private pendingSubShow = null;

    liveLastFetched: Date = null;

    public search() {
        // TODO: OK show the search page or something
        this.shell.showDialog(MessageDialogComponent, 'Search Coming Soon', `Search is not yet implemented! We would search for '${this.searchText}' though if we could!`);
    }

    public searchChanged(value) {
        // console.log('Search changed: '+value);
    }

    searchText: string;

    private currentSliderUrl: string;
    private hoverSliderUrl: string;

    enterSectionLink(el: Element) {
    }

    leaveSectionLink() {
        clearTimeout(this.pendingShow);
    }

    secondaryItemClickEvent(event: Event): void {
        event.preventDefault();
        // this.shell.showDialog(LoginComponent);
        this.shell.showDialog(UserAuthMediatorComponent);
    }

    toggleNotificationDrawer(): void {
        if (!this.notifications.visible) {
            console.log(`Showing nav drawer`);
            window.history.pushState({
                type: 'notifications',
                dialog: 'NotificationListComponent'
            }, '');
            this.notifications.visible = true;
        } else {
            this.notifications.visible = false;
        }
    }

    envColors = {
        development: ['darkgreen', 'white'],
        staging: ['purple', 'white'],
        production: ['#ff1919', 'black']
    };

    hideEnvironmentHint = false;

    get envHintBackgroundColor() {
        if (!environment.showEnvironmentHint)
            return undefined;
        return this.envColors[environment.apiOverride || environment.name][0] || 'yellow';
    }

    get envHintTextColor() {
        if (!environment.showEnvironmentHint)
            return undefined;
        return this.envColors[environment.apiOverride || environment.name][1] || 'black';
    }

    get envMessage() {
        if (!environment.showEnvironmentHint)
            return undefined;

        let names = { development: 'dev', staging: 'stg', production: 'prod' };

        let str = names[environment.name] || environment.name;
        if (environment.apiOverride && environment.apiOverride !== environment.name)
            str += ` | API: ${environment.apiOverride}`

        return str;
    }

    get notificationsCount() {
        return this.notifications.unseen;
    }

    get notificationsVisible() {
        return this.notifications.visible;
    }

    async loadNotifications() {
        await this.userService.ready;
        if (!await this.shell.hasFeature('apps.web.enable_server_notifications'))
            return;

        if (!this.user)
            return;

        const notificationList: PagedArray<ApiNotification> = await this.notificationApi.getNotifications(1).toPromise();

        this.notifications.totalItems = notificationList.total;

        notificationList.reverse().forEach(n => {
            const notification: NotificationItem = {
                id: n.id,
                category: <any>n.category,
                text: n.text,
                url: n.url,
                open_in_new_tab: n.open_in_new_tab,
                description: n.text,
                timestamp: new Date(n.date),
                icon: n.icon,
                extra_actions: n.extra_actions,
                style: n.style as any,
                image: n.image
            }
            this.notifications.add(notification, !n.read);
        })
    }

    private notificationPoll;
    async csrNotificationsUpdate() {
        if (typeof window === 'undefined')
            return;

        if (!await this.shell.hasFeature('apps.web.enable_server_notifications'))
            return;

        setTimeout(() => {
            clearInterval(this.notificationPoll);
            this.notificationPoll = setInterval(() => this.loadNotifications(), (60 + Math.random() * 10 - 5) * 1000)
        }, (30 + Math.random() * 10 - 5) * 1000);
    }

}
